import { DndItemWithId, DndMode, useDndStore } from "designSystem/dnd/index";
import { orderBy, throttle } from "lodash";
import { useRef } from "react";
import { ConnectDragSource, ConnectDropTarget, DragSourceMonitor, DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import { useDndOrderedStore } from "./store/dnd-ordered-store";
import { DndDropFunction, DndEndFunction, DndItem, DndOrdererHookData } from "./types";
import { disableDojoDnd, enableDojoDnd } from "./utilities/manage-legacy-dnd";

interface DragWithPosition {
	/** drag source for dnd framework to connect to ref */
	drag: ConnectDragSource;
	/** handler id that react-dnd assigns each drag source */
	handlerId: string;
	/** is the user dragging boolean flag */
	isDragging: boolean;
}

/** use drag and get a position back as the user drags */
export const useDragWithPosition = <T extends DndItemWithId>(
	/** specifies whether the item can initiate a drag */
	canDrag: boolean,
	/** item to drag */
	dragItem: T,
	/** type of item being dragged */
	dragType: string,
	/** drop zone is full screen or local to drop wrapper component */
	isFullScreen: boolean,
	/** this is called when the dragging stops */
	onEndDrag: DndEndFunction<T>,
): DragWithPosition => {
	const { endDrag, startDrag } = useDndStore();
	const [{ handlerId, isDragging }, drag] = useDrag({
		type: dragType,
		canDrag,
		collect: (monitor: DragSourceMonitor<T, T>) => ({
			handlerId: monitor.getHandlerId(),
			isDragging: monitor.isDragging(),
		}),
		item: (monitor: DragSourceMonitor<T, T>) => {
			startDrag(dragItem, isFullScreen);
			return dragItem;
		},
		end: (draggedItem: T, monitor: DragSourceMonitor<T, T>) => {
			endDrag();
			return onEndDrag?.(draggedItem, monitor);
		},
	}, [dragItem.id]);

	return { drag, handlerId: handlerId?.toString(), isDragging };
};

interface DropWithPosition {
	/** can the user drop at the target flag */
	canDrop: boolean;
	/** wrapper for the drop target ref to get dustbin capability */
	drop: ConnectDropTarget;
	/** internal drop target id that react-dnd assigns */
	handlerId: string;
	/** is the user over the main target */
	isOver: boolean;
	/** is the user over the shallow target */
	isOverShallow: boolean;
}

/** use drop and get a position back as the mouse hovers over the drop target */
export const useDropWithPosition = <T extends DndItemWithId>(
	/** dnd mode */
	mode: DndMode,
	/** types of items that can be accepted in the drop target */
	types: string[],
	/** called when a compatible item is dropped on the target */
	onDrop: DndDropFunction<T>,
	/** called when a compatible item is hovered on the target */
	onHover?: DndDropFunction<T>,
): DropWithPosition => {
	const {
		endDrag,
		multiSelectItemArray,
		onDropDefault,
		onHoverDefault,
		previewList,
		selectedDivider,
		setIsLoading,
		setPosition,
	} = useDndStore();
	const lastIndex = useRef<number>(0);
	const [{ canDrop, handlerId, isOver, isOverShallow }, drop] = useDrop({
		accept: types,
		collect: (monitor: DropTargetMonitor<T, T>) => ({
			handlerId: monitor.getHandlerId(),
			isOver: monitor.isOver(),
			isOverShallow: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop(),
		}),
		// TODO: maybe, in the future, we will want to add the dropped item to the store, for now, there's no need to do that
		drop(item: T, monitor: DropTargetMonitor<T, T>): undefined {
			if (selectedDivider && multiSelectItemArray.length) {
				const { index, parentId } = selectedDivider;
				const sortedItems = orderBy(multiSelectItemArray, ["order"], ["desc"]);
				if (onDrop) {
					onDrop(sortedItems, monitor, index, parentId, previewList);
				} else {
					onDropDefault(sortedItems, monitor, index, parentId);
				}
			} else if (selectedDivider) {
				const { index, parentId } = selectedDivider;
				const droppedItem = onDrop ?
					onDrop(item, monitor, index, parentId, previewList) :
					onDropDefault(item, monitor, index, parentId);

				// handle the case where the dropped item is a promise
				const droppedItemAsPromise = droppedItem as Promise<T>;
				if (droppedItemAsPromise?.then) {
					setIsLoading(true);
					droppedItemAsPromise.then((result) => {
						setIsLoading(false);
					});
				}
			} else {
				onDrop(item, monitor);
			}

			if (mode === DndMode.Divider) { 
				endDrag();
			}
		},
		hover(item: T, monitor: DropTargetMonitor<T, T>): void {
			setPosition(monitor.getClientOffset());
			if (mode === DndMode.Normal) {
				if (selectedDivider && lastIndex.current !== selectedDivider.index) {
					const { index, parentId } = selectedDivider;
					onHover ? onHover(item, monitor, index, parentId) : onHoverDefault(item, monitor, index, parentId);
					lastIndex.current = selectedDivider.index;
				}
			}
		},
	});

	return { canDrop, drop, handlerId: handlerId?.toString(), isOver, isOverShallow };
};

/** hook to get refs for drag and drop functionality */
export function useDndOrderedOnItem(originalIndex: number): DndOrdererHookData {
	const {
		dndType,
		getCurrentIndex,
		hoverThrottle,
		onDrag,
		onDragCancel,
		onDragComplete,
	} = useDndOrderedStore();

	const currentIndex = getCurrentIndex(originalIndex);

	const [{ isDragging }, dragHandleRef, dragItemRef] = useDrag(
		() => ({
			collect: (monitor) => ({
				isDragging: monitor.isDragging(),
			}),
			end: (item, monitor): void => {
				enableDojoDnd();
				if (monitor.didDrop()) {
					onDragComplete(item.originalIndex);
				} else {
					onDragCancel();
				}
			},
			item: (): { originalIndex: number } => {
				disableDojoDnd();
				return { originalIndex };
			},
			options: { dropEffect: "move" },
			type: dndType,
		}),
		[originalIndex, onDragCancel, onDragComplete],
	);

	const [, drop] = useDrop(
		() => ({
			accept: dndType,
			hover: throttle((item: DndItem, monitor) => {
				// test whether the hover happens over only the current target, otherwise, would get too many unnecessary calls
				if (item.originalIndex !== originalIndex && monitor.isOver({ shallow: true })) {
					onDrag(item.originalIndex, currentIndex);
				}
			}, hoverThrottle),
		}),
		[currentIndex, originalIndex],
	);

	return [isDragging, dragItemRef, dragHandleRef, drop];
}
